import { CountrySelectorProps } from './props';
import { AutocompleteMenuSelector } from '../AutocompleteMenuSelector';
import {
  SelectInputWithImage,
  SelectOptionWithImage,
} from '@starsoft/common/components';
import { SettingsService } from '@starsoft/common/services/SettingsService';

/**
 * Component that renders a country selector with autocomplete functionality
 * Displays country flags and names in a searchable dropdown menu
 *
 * @component
 * @param {Object} props - Component props
 * @param {Function} props.setValue - Callback function to set selected country value
 * @param {Array} props.options - Array of country options to display
 * @param {Object} props.currentValue - Currently selected country object
 * @param {boolean} props.isDisabled - Whether the selector is disabled
 * @param {boolean} props.hasNextPage - Whether there are more options to load
 * @param {boolean} props.isFetchingNextPage - Whether next page of options is being fetched
 * @param {boolean} props.isLoading - Whether initial options are being loaded
 * @param {Function} props.loadMore - Callback to load more country options
 * @param {Function} props.onSearchChange - Callback when search input changes
 * @param {boolean} props.isContainerColor - Whether to use container background color
 * @returns {JSX.Element} Autocomplete country selector with flags and names
 */
export function CountrySelector({
  setValue,
  options,
  currentValue,
  isDisabled,
  hasNextPage,
  isFetchingNextPage,
  isLoading,
  loadMore,
  onSearchChange,
  isContainerColor,
}: CountrySelectorProps): JSX.Element {
  return (
    <AutocompleteMenuSelector
      options={options ?? []}
      getOptionLabel={e => e?.name}
      getOptionValue={e => e?.id}
      value={currentValue?.id}
      setValue={setValue}
      hasNextPage={hasNextPage}
      filterBy={['name']}
      onSearchChange={onSearchChange}
      isAsync
      loadMore={loadMore}
      isContainerColor={isContainerColor}
      isSmallerDrawer
      disabled={isDisabled}
      loading={isLoading}
      loadingMore={isFetchingNextPage}
      customComponents={{
        customOption: ({ handleSelect, option, selected }) => (
          <SelectOptionWithImage
            handleSelect={handleSelect}
            option={option}
            selected={selected}
            image={`${SettingsService.env.S3_BUCKET_BASE_URL}/flags/${option?.image}`}
            label={option?.name}
          />
        ),
        customInput: ({ handleOpen, open, disabled, option }) => (
          <SelectInputWithImage
            handleOpen={handleOpen}
            image={`${SettingsService.env.S3_BUCKET_BASE_URL}/flags/${option?.image}`}
            label={option?.dialCode}
            open={open}
            disabled={disabled}
          />
        ),
      }}
    />
  );
}
